(function () {
  function parseBoolean(value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      return value.toLowerCase() === "true" || value === "1";
    }
    if (typeof value === "number") return value === 1;
    return false;
  }

  function parseNumber(value) {
    if (value === undefined || value === null || value === "") return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }

  function resolveLengthViaComputedStyle(lengthString, element) {
    if (typeof lengthString !== "string" || lengthString.trim() === "")
      return undefined;
    const host =
      element && element.nodeType === 1 ? element : document.documentElement;
    const scratch = document.createElement("div");
    scratch.style.position = "absolute";
    scratch.style.visibility = "hidden";
    scratch.style.left = "-9999px";
    scratch.style.top = "0";
    scratch.style.height = "0";
    scratch.style.padding = "0";
    scratch.style.border = "0";
    scratch.style.margin = "0";
    scratch.style.paddingLeft = lengthString;
    try {
      host.appendChild(scratch);
      const computed = getComputedStyle(scratch).paddingLeft;
      const n = parseFloat(computed);
      return Number.isFinite(n) ? n : undefined;
    } catch (e) {
      return undefined;
    } finally {
      if (scratch.parentNode) scratch.parentNode.removeChild(scratch);
    }
  }

  function parseLength(value, element, depth) {
    // Resolve numeric, px, rem/em, and CSS variable values to pixels
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "number") return value;
    const recursionDepth = depth === undefined ? 0 : depth;
    if (recursionDepth > 3) return undefined;
    if (typeof value === "string") {
      let s = value.trim();
      // CSS variable: var(--token[, fallback])
      if (s.startsWith("var(")) {
        const match = s.match(/^var\(\s*([^,\s)]+)\s*(?:,\s*([^)]+)\s*)?\)$/);
        if (match) {
          const propName = match[1];
          const fallbackRaw = match[2];
          const refNode = element || document.documentElement;
          const computed = getComputedStyle(refNode)
            .getPropertyValue(propName)
            .trim();
          const candidate = computed || (fallbackRaw ? fallbackRaw.trim() : "");
          if (candidate) {
            return parseLength(candidate, refNode, recursionDepth + 1);
          }
          return undefined;
        }
      }
      // Pure number (interpreted as px)
      if (/^[+-]?\d+(?:\.\d+)?$/.test(s)) {
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : undefined;
      }
      // px
      if (s.endsWith("px")) {
        const n = parseFloat(s.slice(0, -2));
        return Number.isFinite(n) ? n : undefined;
      }
      // rem
      if (s.endsWith("rem")) {
        const n = parseFloat(s.slice(0, -3));
        const rootFont = parseFloat(
          getComputedStyle(document.documentElement).fontSize
        );
        return Number.isFinite(n) && Number.isFinite(rootFont)
          ? n * rootFont
          : undefined;
      }
      // em
      if (s.endsWith("em")) {
        const n = parseFloat(s.slice(0, -2));
        const refNode = element || document.documentElement;
        const fontSize = parseFloat(getComputedStyle(refNode).fontSize);
        return Number.isFinite(n) && Number.isFinite(fontSize)
          ? n * fontSize
          : undefined;
      }
      // Fallback: try to evaluate any valid CSS length expression (calc/clamp/vw/vh/var chains)
      const evaluated = resolveLengthViaComputedStyle(s, element);
      if (Number.isFinite(evaluated)) return evaluated;
      return undefined;
    }
    return undefined;
  }

  function getScrubValue(mode, value) {
    const m = (mode || "number").toString();
    if (m === "true") return true;
    if (m === "false") return false;
    const n = parseNumber(value);
    return Number.isFinite(n) ? n : false;
  }

  function buildConfigFromDataset(el) {
    const ds = el.dataset;
    // Read breakpoints payload if present
    let bp = undefined;
    if (typeof ds.adBreakpoints === "string" && ds.adBreakpoints) {
      try {
        bp = JSON.parse(ds.adBreakpoints);
      } catch (e) {
        bp = undefined;
      }
    }

    // Helper: pick value from breakpoints map based on current viewport width
    function pickBreakpointValue(key) {
      if (!bp || !bp.breakpoints || typeof window === "undefined")
        return undefined;
      const width =
        window.innerWidth || document.documentElement.clientWidth || 0;
      const entries = Object.entries(bp.breakpoints)
        .map(([w, vals]) => [Number(w), vals])
        .filter(([w]) => Number.isFinite(w))
        .sort((a, b) => a[0] - b[0]);
      if (entries.length === 0) return undefined;
      const isMin = bp.mediaQuery === "min";
      let chosen;
      if (isMin) {
        for (let i = 0; i < entries.length; i++) {
          const [w, vals] = entries[i];
          if (width >= w) chosen = vals;
        }
      } else {
        for (let i = 0; i < entries.length; i++) {
          const [w, vals] = entries[i];
          if (width <= w) {
            chosen = vals;
            break;
          }
        }
      }
      if (!chosen || typeof chosen !== "object") return undefined;
      return chosen[key];
    }

    const insetXFromBp = pickBreakpointValue("insetX");
    const insetYFromBp = pickBreakpointValue("insetY");
    const radiusFromBp = pickBreakpointValue("radius");
    const startOpenFromBp = pickBreakpointValue("startOpen");
    const endOpenFromBp = pickBreakpointValue("endOpen");
    const startCloseFromBp = pickBreakpointValue("startClose");
    const endCloseFromBp = pickBreakpointValue("endClose");

    return {
      insetX: parseLength(
        insetXFromBp !== undefined ? insetXFromBp : ds.adInsetX,
        el
      ),
      insetY: parseLength(
        insetYFromBp !== undefined ? insetYFromBp : ds.adInsetY,
        el
      ),
      radius: parseLength(
        radiusFromBp !== undefined ? radiusFromBp : ds.adRadius,
        el
      ),
      scrubOpen: getScrubValue(ds.adScrubOpenMode, ds.adScrubOpenValue),
      scrubClose: getScrubValue(ds.adScrubCloseMode, ds.adScrubCloseValue),
      closeAtEnd: parseBoolean(ds.adCloseAtEnd),
      reverse: parseBoolean(ds.adReverse),
      markers: parseBoolean(ds.adMarkers),
      duration: (function () {
        const seconds = parseNumber(ds.adDuration);
        return Number.isFinite(seconds) ? seconds : 1.2;
      })(),
      delay: (function () {
        const seconds = parseNumber(ds.adDelay);
        return Number.isFinite(seconds) ? seconds : 0;
      })(),
      startOpen: startOpenFromBp,
      endOpen: endOpenFromBp,
      startClose: startCloseFromBp,
      endClose: endCloseFromBp,
      targetChildSelector: ds.adTargetChildSelector,
    };
  }

  // Helpers to keep main initializer compact and readable
  function computeDerivedValues(CONFIG) {
    const INSET_X_PX = Number.isFinite(CONFIG.insetX)
      ? CONFIG.insetX
      : undefined;
    const INSET_Y_PX = Number.isFinite(CONFIG.insetY)
      ? CONFIG.insetY
      : undefined;
    const RADIUS_PX = Number.isFinite(CONFIG.radius)
      ? CONFIG.radius
      : undefined;

    const SCRUB_OPEN =
      CONFIG.scrubOpen === undefined
        ? false
        : CONFIG.scrubOpen === true || CONFIG.scrubOpen === false
        ? CONFIG.scrubOpen
        : Number.isFinite(CONFIG.scrubOpen)
        ? CONFIG.scrubOpen
        : false;

    const SCRUB_CLOSE =
      CONFIG.scrubClose === undefined
        ? false
        : CONFIG.scrubClose === true || CONFIG.scrubClose === false
        ? CONFIG.scrubClose
        : Number.isFinite(CONFIG.scrubClose)
        ? CONFIG.scrubClose
        : false;

    const CLOSE_AT_END = Boolean(CONFIG.closeAtEnd);
    const REVERSE = Boolean(CONFIG.reverse);

    return {
      INSET_X_PX,
      INSET_Y_PX,
      RADIUS_PX,
      SCRUB_OPEN,
      SCRUB_CLOSE,
      CLOSE_AT_END,
      REVERSE,
    };
  }

  function initializeState(derived) {
    const hasInsetX = Number.isFinite(derived.INSET_X_PX);
    const hasInsetY = Number.isFinite(derived.INSET_Y_PX);
    const hasRadius = Number.isFinite(derived.RADIUS_PX);

    const state = { insetX: 0, insetY: 0, radius: 0 };
    if (derived.REVERSE) {
      if (hasInsetX) state.insetX = derived.INSET_X_PX;
      if (hasInsetY) state.insetY = derived.INSET_Y_PX;
      if (hasRadius) state.radius = derived.RADIUS_PX;
    }

    return { state, hasInsetX, hasInsetY, hasRadius };
  }

  function createApplyClipPath(target, hasInsetX, hasInsetY, hasRadius, state) {
    return () => {
      // Apply clipPath if any inset dimension is provided; fallback to borderRadius-only when no inset
      if (hasInsetX || hasInsetY) {
        const y = hasInsetY ? state.insetY : 0;
        const x = hasInsetX ? state.insetX : 0;
        const top = `${y}px`;
        const right = `${x}px`;
        const bottom = `${y}px`;
        const left = `${x}px`;
        const maybeRound = hasRadius ? ` round ${state.radius}px` : "";
        const insetValue = `inset(${top} ${right} ${bottom} ${left}${maybeRound})`;
        target.style.clipPath = insetValue;
        target.style.webkitClipPath = insetValue;
        // Ensure borderRadius isn't fighting clipPath when radius is driven via clipPath round
        if (hasRadius) target.style.borderRadius = "";
      } else if (hasRadius) {
        target.style.borderRadius = `${state.radius}px`;
      }
    };
  }

  function applyInitialStyles(hasInsetX, hasInsetY, hasRadius, applyClipPath) {
    if (hasInsetX || hasInsetY || hasRadius) applyClipPath();
  }

  function createEventTriggerConfig(target, markers) {
    return {
      trigger: target,
      start: "top bottom-=100px",
      end: "bottom 40%",
      markers: Boolean(markers),
    };
  }

  function buildScrollTriggerCfg(target, start, end, markers, scrub) {
    return {
      trigger: target,
      start,
      end,
      markers: Boolean(markers),
      scrub,
    };
  }

  function assignPhaseValues(
    phase,
    reverse,
    hasInsetX,
    hasInsetY,
    hasRadius,
    INSET_X_PX,
    INSET_Y_PX,
    RADIUS_PX,
    from,
    to
  ) {
    const isOpen = phase === "open";
    const fromInsetX = isOpen
      ? reverse
        ? INSET_X_PX
        : 0
      : reverse
      ? 0
      : INSET_X_PX;
    const toInsetX = isOpen
      ? reverse
        ? 0
        : INSET_X_PX
      : reverse
      ? INSET_X_PX
      : 0;
    const fromInsetY = isOpen
      ? reverse
        ? INSET_Y_PX
        : 0
      : reverse
      ? 0
      : INSET_Y_PX;
    const toInsetY = isOpen
      ? reverse
        ? 0
        : INSET_Y_PX
      : reverse
      ? INSET_Y_PX
      : 0;
    const fromRadius = isOpen
      ? reverse
        ? RADIUS_PX
        : 0
      : reverse
      ? 0
      : RADIUS_PX;
    const toRadius = isOpen
      ? reverse
        ? 0
        : RADIUS_PX
      : reverse
      ? RADIUS_PX
      : 0;

    if (hasInsetX) {
      from.insetX = fromInsetX;
      to.insetX = toInsetX;
    }
    if (hasInsetY) {
      from.insetY = fromInsetY;
      to.insetY = toInsetY;
    }
    if (hasRadius) {
      from.radius = fromRadius;
      to.radius = toRadius;
    }
  }

  function setupScrubPhase(opts) {
    const {
      gsap,
      state,
      applyClipPath,
      target,
      phase,
      scrub,
      reverse,
      hasInsetX,
      hasInsetY,
      hasRadius,
      INSET_X_PX,
      INSET_Y_PX,
      RADIUS_PX,
      markers,
      startOpen,
      endOpen,
      startClose,
      endClose,
    } = opts;
    const fromVals = {};
    const start =
      phase === "open"
        ? startOpen || "top bottom-=100px"
        : startClose || "bottom 50%";
    const end =
      phase === "open"
        ? endOpen || "top bottom-=400px"
        : endClose || "bottom 40%";
    const toVals = {
      ease: "none",
      onUpdate: applyClipPath,
      immediateRender: false,
      scrollTrigger: buildScrollTriggerCfg(
        target,
        start,
        end,
        phase === "close" ? markers : undefined,
        scrub
      ),
    };
    assignPhaseValues(
      phase,
      reverse,
      hasInsetX,
      hasInsetY,
      hasRadius,
      INSET_X_PX,
      INSET_Y_PX,
      RADIUS_PX,
      fromVals,
      toVals
    );
    gsap.fromTo(state, fromVals, toVals);
  }

  function setupEventFallbacksCloseAtEnd(opts) {
    const {
      ScrollTrigger,
      gsap,
      element,
      eventTriggerConfig,
      applyClipPath,
      CONFIG,
      reverse,
      hasInsetX,
      hasInsetY,
      hasRadius,
      INSET_X_PX,
      INSET_Y_PX,
      RADIUS_PX,
      scrubOpen,
      scrubClose,
      state,
    } = opts;
    if (scrubOpen === false || scrubClose === false) {
      const fromOpenEvt = {};
      const toOpenEvt = {
        duration: CONFIG.duration,
        ease: "power2.out",
        onUpdate: applyClipPath,
        delay: CONFIG.delay,
      };
      assignPhaseValues(
        "open",
        reverse,
        hasInsetX,
        hasInsetY,
        hasRadius,
        INSET_X_PX,
        INSET_Y_PX,
        RADIUS_PX,
        fromOpenEvt,
        toOpenEvt
      );

      const fromCloseEvt = {};
      const toCloseEvt = {
        duration: CONFIG.duration,
        ease: "power2.in",
        onUpdate: applyClipPath,
        delay: CONFIG.delay,
      };
      assignPhaseValues(
        "close",
        reverse,
        hasInsetX,
        hasInsetY,
        hasRadius,
        INSET_X_PX,
        INSET_Y_PX,
        RADIUS_PX,
        fromCloseEvt,
        toCloseEvt
      );

      const callbacks = {};
      if (scrubOpen === false) {
        callbacks.onEnter = () => {
          if (element.__adOpenPlayed) return;
          element.__adOpenPlayed = true;
          gsap.fromTo(state, fromOpenEvt, toOpenEvt);
        };
      }
      if (scrubClose === false) {
        callbacks.onLeave = () => {
          if (element.__adClosePlayed) return;
          element.__adClosePlayed = true;
          gsap.fromTo(state, fromCloseEvt, toCloseEvt);
        };
      }
      if (Object.keys(callbacks).length) {
        ScrollTrigger.create({
          ...eventTriggerConfig,
          ...callbacks,
        });
      }
    }
  }

  function setupNoCloseAtEndEventOrScrub(opts) {
    const {
      ScrollTrigger,
      gsap,
      element,
      eventTriggerConfig,
      applyClipPath,
      CONFIG,
      reverse,
      hasInsetX,
      hasInsetY,
      hasRadius,
      INSET_X_PX,
      INSET_Y_PX,
      RADIUS_PX,
      scrubOpen,
      state,
      target,
      markers,
      startOpen,
      endOpen,
    } = opts;
    if (scrubOpen === false) {
      const fromSingleEvt = {};
      const toSingleEvt = {
        duration: CONFIG.duration,
        ease: "power2.out",
        onUpdate: applyClipPath,
        delay: CONFIG.delay,
      };
      assignPhaseValues(
        "open",
        reverse,
        hasInsetX,
        hasInsetY,
        hasRadius,
        INSET_X_PX,
        INSET_Y_PX,
        RADIUS_PX,
        fromSingleEvt,
        toSingleEvt
      );

      ScrollTrigger.create({
        ...eventTriggerConfig,
        start: startOpen || eventTriggerConfig.start,
        end: endOpen || eventTriggerConfig.end,
        onEnter: () => {
          if (element.__adSinglePlayed) return;
          element.__adSinglePlayed = true;
          gsap.fromTo(state, fromSingleEvt, toSingleEvt);
        },
      });
    } else {
      const fromSingle = {};
      const toSingle = {
        duration: CONFIG.duration,
        ease: "none",
        onUpdate: applyClipPath,
        scrollTrigger: {
          trigger: target,
          start: startOpen || "top bottom-=100px",
          end: endOpen || "bottom 40%",
          markers: Boolean(markers),
          scrub: scrubOpen,
        },
      };
      assignPhaseValues(
        "open",
        reverse,
        hasInsetX,
        hasInsetY,
        hasRadius,
        INSET_X_PX,
        INSET_Y_PX,
        RADIUS_PX,
        fromSingle,
        toSingle
      );
      gsap.fromTo(state, fromSingle, toSingle);
    }
  }

  function initAnimationDivider(element, config) {
    if (!element) return;
    if (
      typeof window.gsap === "undefined" ||
      typeof window.ScrollTrigger === "undefined"
    )
      return;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    const CONFIG = config || {};
    const derived = computeDerivedValues(CONFIG);

    let target = element;
    if (
      config &&
      typeof config.targetChildSelector === "string" &&
      config.targetChildSelector.trim() !== ""
    ) {
      const sel = config.targetChildSelector.trim();
      try {
        // support "> .class" by scoping to direct children when selector starts with ">"
        if (sel.startsWith(">")) {
          const direct = element.querySelectorAll(":scope > " + sel.slice(1));
          if (direct && direct.length > 0) target = direct[0];
        } else {
          const found = element.querySelector(sel);
          if (found) target = found;
        }
      } catch (e) {
        // ignore invalid selectors silently
      }
    }
    const { state, hasInsetX, hasInsetY, hasRadius } = initializeState(derived);

    // one-time flags for event-driven flows
    if (element.__adOpenPlayed === undefined) element.__adOpenPlayed = false;
    if (element.__adClosePlayed === undefined) element.__adClosePlayed = false;
    if (element.__adSinglePlayed === undefined)
      element.__adSinglePlayed = false;

    const applyClipPath = createApplyClipPath(
      target,
      hasInsetX,
      hasInsetY,
      hasRadius,
      state
    );
    applyInitialStyles(hasInsetX, hasInsetY, hasRadius, applyClipPath);

    const eventTriggerConfig = createEventTriggerConfig(target, CONFIG.markers);
    // Optional custom start/end positions from dataset or breakpoints config
    const startOpen = CONFIG.startOpen || element.dataset.adStartOpen;
    const endOpen = CONFIG.endOpen || element.dataset.adEndOpen;
    const startClose = CONFIG.startClose || element.dataset.adStartClose;
    const endClose = CONFIG.endClose || element.dataset.adEndClose;

    if (hasInsetX || hasInsetY || hasRadius) {
      if (derived.CLOSE_AT_END) {
        if (derived.SCRUB_OPEN !== false) {
          setupScrubPhase({
            gsap,
            state,
            applyClipPath,
            target,
            phase: "open",
            scrub: derived.SCRUB_OPEN,
            reverse: derived.REVERSE,
            hasInsetX,
            hasInsetY,
            hasRadius,
            INSET_X_PX: derived.INSET_X_PX,
            INSET_Y_PX: derived.INSET_Y_PX,
            RADIUS_PX: derived.RADIUS_PX,
            startOpen,
            endOpen,
            startClose,
            endClose,
          });
        }

        if (derived.SCRUB_CLOSE !== false) {
          setupScrubPhase({
            gsap,
            state,
            applyClipPath,
            target,
            phase: "close",
            scrub: derived.SCRUB_CLOSE,
            reverse: derived.REVERSE,
            hasInsetX,
            hasInsetY,
            hasRadius,
            INSET_X_PX: derived.INSET_X_PX,
            INSET_Y_PX: derived.INSET_Y_PX,
            RADIUS_PX: derived.RADIUS_PX,
            markers: CONFIG.markers,
            startOpen,
            endOpen,
            startClose,
            endClose,
          });
        }

        setupEventFallbacksCloseAtEnd({
          ScrollTrigger,
          gsap,
          element,
          eventTriggerConfig,
          applyClipPath,
          CONFIG,
          reverse: derived.REVERSE,
          hasInsetX,
          hasInsetY,
          hasRadius,
          INSET_X_PX: derived.INSET_X_PX,
          INSET_Y_PX: derived.INSET_Y_PX,
          RADIUS_PX: derived.RADIUS_PX,
          scrubOpen: derived.SCRUB_OPEN,
          scrubClose: derived.SCRUB_CLOSE,
          state,
          startOpen,
          endOpen,
          startClose,
          endClose,
        });
      } else {
        setupNoCloseAtEndEventOrScrub({
          ScrollTrigger,
          gsap,
          element,
          eventTriggerConfig,
          applyClipPath,
          CONFIG,
          reverse: derived.REVERSE,
          hasInsetX,
          hasInsetY,
          hasRadius,
          INSET_X_PX: derived.INSET_X_PX,
          INSET_Y_PX: derived.INSET_Y_PX,
          RADIUS_PX: derived.RADIUS_PX,
          scrubOpen: derived.SCRUB_OPEN,
          state,
          target,
          markers: CONFIG.markers,
          startOpen,
          endOpen,
        });
      }
    }
  }

  function initAll() {
    const nodes = document.querySelectorAll('[data-animation-divider="true"]');
    nodes.forEach(function (el) {
      if (el.__adInitialized) return;

      el.__adInitialized = true;
      const cfg = buildConfigFromDataset(el);
      initAnimationDivider(el, cfg);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
